import { jsx, jsxs } from "react/jsx-runtime";
import { isSVGIcon, Icon, IconSize, SvgIcon, ArdoqIconCategory, IconName } from "@ardoq/icons";
import { forwardRef, useState, useRef, useLayoutEffect } from "react";
import { WithPopover } from "@ardoq/popovers";
import { FlexBox, spaceVariantToValue, Box } from "@ardoq/layout";
import { colors } from "@ardoq/design-tokens";
import { HighlightedText } from "@ardoq/common-helpers";
import { ARDOQ_DEFAULT_FONT_FAMILY } from "@ardoq/typography";
import { sumBy, head, last, memoize } from "lodash";
const EntityIcon = ({
  iconSize = IconSize.MEDIUM,
  imageSrc,
  iconId,
  shapeId,
  color,
  style: propStyle = {}
}) => {
  const isFontAwesomeIcon = iconId && !isSVGIcon(iconId);
  const style = {
    ...propStyle,
    minWidth: iconSize,
    width: iconSize,
    height: iconSize,
    color
  };
  if (imageSrc || isFontAwesomeIcon)
    return /* @__PURE__ */ jsx(
      Icon,
      {
        style,
        src: imageSrc,
        iconName: `fa fa-${iconId}`,
        iconSize
      }
    );
  if (isSVGIcon(iconId) || shapeId)
    return /* @__PURE__ */ jsx(
      SvgIcon,
      {
        style,
        iconId: iconId || shapeId,
        color
      }
    );
  return null;
};
const { FontAwesome } = ArdoqIconCategory;
const emptyIcon = {
  id: "",
  isSVG: false,
  category: FontAwesome,
  label: "None",
  representation: ""
};
const representationDataToEntityIconProps = ({
  isImage: isImageProp,
  value = "",
  icon = emptyIcon,
  style,
  iconSize
}) => {
  const { id, isSVG, category } = icon;
  const isFontAwesome = category === FontAwesome;
  const isImage = isImageProp && value;
  const color = style == null ? void 0 : style.color;
  return {
    style,
    color,
    imageSrc: isImage ? value : void 0,
    iconId: isFontAwesome || isSVG ? id : void 0,
    iconSize
  };
};
const ComponentRepresentation = (props) => /* @__PURE__ */ jsx(EntityIcon, { ...representationDataToEntityIconProps(props) });
const BreadcrumbTrail = forwardRef(
  ({ breadcrumbs, textStyles, highlight }, ref) => /* @__PURE__ */ jsx(FlexBox, { ref, align: "center", flexWrap: false, children: breadcrumbs.map(({ text, icon, gap }, index) => /* @__PURE__ */ jsxs(FlexBox, { align: "center", gap, flexWrap: false, children: [
    /* @__PURE__ */ jsx(Icon, { iconName: icon.name, color: colors.icon, iconSize: icon.size }),
    /* @__PURE__ */ jsx(
      "span",
      {
        style: {
          whiteSpace: "nowrap",
          ...textStyles
        },
        children: /* @__PURE__ */ jsx(HighlightedText, { text, highlight })
      }
    )
  ] }, index)) })
);
const defaultProps = {
  breadcrumbs: [],
  ellipsis: "...",
  leadingIcon: {
    name: IconName.FOLDER,
    size: IconSize.SMALL,
    rightMargin: "xsmall"
  },
  dividerIcon: {
    name: IconName.CHEVRON_RIGHT,
    size: IconSize.SMALL
  },
  textStyles: {
    fontFamily: ARDOQ_DEFAULT_FONT_FAMILY,
    fontWeight: "400",
    fontSize: "12px",
    lineHeight: "16px",
    color: colors.textSubtle
  }
};
const theyFit = (elements, maxWidth) => sumBy(elements, "width") <= maxWidth;
const trimElements = (elements, maxWidth, ellipsis) => {
  if (elements.length === 0) return [ellipsis];
  const withEllipsis = [...elements, ellipsis];
  if (theyFit(withEllipsis, maxWidth)) return withEllipsis;
  return trimElements(elements.slice(0, -1), maxWidth, ellipsis);
};
const ellipsisLayout = (elements, containerWidth, ellipsis) => {
  const leadingElement = head(elements);
  const trailingElement = last(elements);
  const body = trimElements(
    elements.slice(1, -1),
    containerWidth - sumBy([leadingElement, trailingElement], "width"),
    ellipsis
  );
  return [leadingElement, ...body, trailingElement];
};
const measureTextBlockSize = memoize(
  ({ text, style }) => {
    if (typeof document === "undefined") return { width: 0, height: 0 };
    const element = document.createElement("span");
    element.appendChild(document.createTextNode(text));
    if (style) {
      Object.assign(element.style, style);
    }
    document.body.appendChild(element);
    const { width, height } = element.getBoundingClientRect();
    element.remove();
    return { width: width || 0, height: height || 0 };
  },
  JSON.stringify
);
const spaceVariantToPixels = (variant) => {
  const value = spaceVariantToValue(variant);
  if (!value) return 0;
  return parseInt(value, 10);
};
const mesureSegment = (segment, textStyles) => {
  const { width, height } = measureTextBlockSize({
    text: segment.text,
    style: textStyles
  });
  const gap = spaceVariantToPixels(segment.gap);
  return {
    ...segment,
    width: width + segment.icon.size + gap,
    height
  };
};
const textToSegment = (text) => ({
  text
});
const addIconToSegment = (segment, icon) => ({
  ...segment,
  icon,
  gap: icon.rightMargin
});
const breadcrumbsLayout = (props, availableWidth) => {
  const { breadcrumbs, ellipsis, leadingIcon, dividerIcon, textStyles } = props;
  const segments = breadcrumbs.map(textToSegment).map(
    (segment, index) => addIconToSegment(segment, index === 0 ? leadingIcon : dividerIcon)
  );
  if (segments.length <= 2) return { breadcrumbs: segments };
  const segmentsWithSizes = segments.map(
    (segment) => mesureSegment(segment, textStyles)
  );
  if (theyFit(segmentsWithSizes, availableWidth))
    return { breadcrumbs: segments };
  const ellipsisWithSizes = mesureSegment(
    addIconToSegment(textToSegment(ellipsis), dividerIcon),
    textStyles
  );
  const segmentsWithEllipsis = ellipsisLayout(
    segmentsWithSizes,
    availableWidth,
    ellipsisWithSizes
  );
  return {
    breadcrumbs: segmentsWithEllipsis,
    popoverContent: segments
  };
};
const Breadcrumbs = (props) => {
  var _a;
  const [layout, setLayout] = useState({ breadcrumbs: [] });
  const ref = useRef(null);
  const {
    breadcrumbs,
    textStyles = defaultProps.textStyles,
    leadingIcon = defaultProps.leadingIcon,
    highlight
  } = props;
  useLayoutEffect(() => {
    var _a2;
    const layout2 = breadcrumbsLayout(
      {
        ...defaultProps,
        leadingIcon,
        breadcrumbs,
        textStyles
      },
      ((_a2 = ref.current) == null ? void 0 : _a2.offsetWidth) || 0
    );
    setLayout(layout2);
  }, [
    (_a = ref.current) == null ? void 0 : _a.offsetWidth,
    setLayout,
    breadcrumbs,
    textStyles,
    leadingIcon
  ]);
  return /* @__PURE__ */ jsx(
    WithPopover,
    {
      content: layout.popoverContent ? () => /* @__PURE__ */ jsx(
        BreadcrumbTrail,
        {
          breadcrumbs: layout.popoverContent,
          textStyles,
          highlight
        }
      ) : void 0,
      children: /* @__PURE__ */ jsx(Box, { children: /* @__PURE__ */ jsx(
        BreadcrumbTrail,
        {
          ref,
          breadcrumbs: layout.breadcrumbs,
          textStyles,
          highlight
        }
      ) })
    }
  );
};
export {
  Breadcrumbs as Breadcrumbs2,
  ComponentRepresentation,
  EntityIcon,
  representationDataToEntityIconProps
};
//# sourceMappingURL=index.js.map
